import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import loginBg from "../../assets/images/login-bg.svg";
import {
  resetPasswordWithToken,
  validateResetToken,
  ResetPasswordRequest,
  ValidateTokenRequest,
} from "../../service/api";

const ResetPassForForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [timeLeft, setTimeLeft] = useState(20 * 60);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setIsExpired(true);
      setIsValidating(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      // Fix: Ensure token is not null
      if (!token) {
        setIsExpired(true);
        return;
      }

      const requestData: ValidateTokenRequest = { token };
      const response = await validateResetToken(requestData);
      // Check for success using multiple possible response structures
      if (response.data?.success) {
        const email = response.data.data?.data?.email || "";
        setUserEmail(email);

        const decoded = JSON.parse(atob(token.split(".")[1]));
        const expiresAt = decoded.exp * 1000;
        const remaining = Math.max(
          0,
          Math.floor((expiresAt - Date.now()) / 1000)
        );
        setTimeLeft(remaining);
      } else {
        setIsExpired(true);
        setError(
          response.data?.resMessage ||
            (response.data.message as string) ||
            "Invalid reset token"
        );
      }
    } catch (error: any) {
      console.error("Token validation error:", error);
      setIsExpired(true);
      setError(
        error.response?.data?.resMessage ||
          (error.response?.data?.message as string) ||
          "Invalid or expired reset token"
      );
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords don't match!");
      return;
    }

    // Validate password strength
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError(
        "Password must be at least 8 characters with uppercase, lowercase, number, and special character."
      );
      return;
    }

    setIsLoading(true);

    try {
      const requestData: ResetPasswordRequest = {
        token: token!,
        new_password: password,
      };

      const response = await resetPasswordWithToken(requestData);
      // Check for success using multiple possible response structures
      if (response.data?.success) {
        alert("Password reset successfully!");
        navigate("/login");
      } else {
        setError(
          response.data?.resMessage ||
            (response.data.message as string) ||
            "Failed to reset password"
        );
      }
    } catch (error: any) {
      console.error("Error resetting password:", error);
      setError(
        error.response?.data?.resMessage ||
          (error.response?.data?.message as string) ||
          "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (isValidating) {
    return (
      <div className="container-fluid p-0 h-100">
        <div className="row no-gutter h-100 full-height">
          <div
            className="col-sm-7 d-none d-lg-flex bg"
            style={{ backgroundImage: "url('" + loginBg + "')" }}
          ></div>
          <div className="col-lg-5 bg-white">
            <div className="loginCont">
              <div className="row no-gutters h-100 align-items-center">
                <div className="col-sm-12 mx-auto">
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Validating reset link...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isExpired || !token) {
    return (
      <div className="container-fluid p-0 h-100">
        <div className="row no-gutter h-100 full-height">
          <div
            className="col-sm-7 d-none d-lg-flex bg"
            style={{ backgroundImage: "url('" + loginBg + "')" }}
          ></div>
          <div className="col-lg-5 bg-white">
            <div className="loginCont">
              <div className="row no-gutters h-100 align-items-center">
                <div className="col-sm-12 mx-auto">
                  <div className="alert alert-danger text-center">
                    <h4>Link Expired or Invalid</h4>
                    <p className="mb-3">
                      {error ||
                        "This password reset link has expired or is invalid. Please request a new one."}
                    </p>
                    <a href="/forgot-password" className="btn btn-primary">
                      Request New Link
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0 h-100">
      <div className="row no-gutters h-100 full-height">
        <div
          className="col-sm-7 d-none d-lg-flex bg"
          style={{ backgroundImage: "url('" + loginBg + "')" }}
        ></div>
        <div className="col-lg-5 bg-white">
          <div className="loginCont">
            <div className="row no-gutters h-100 align-items-center">
              <div className="col-sm-12 mx-auto">
                <div className="login-form">
                  <h2 className="mb-3">Reset Password</h2>

                  {error && <div className="alert alert-danger">{error}</div>}

                  {userEmail && (
                    <div className="alert alert-info mb-3">
                      Resetting password for: <strong>{userEmail}</strong>
                    </div>
                  )}

                  <div className="alert alert-warning mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Link expires in:</span>
                      <strong
                        className={`${timeLeft < 300 ? "text-danger" : ""}`}
                      >
                        {formatTime(timeLeft)}
                      </strong>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="form-group mb-3">
                      <label htmlFor="password" className="form-label">
                        New Password
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          id="password"
                          placeholder="Enter new password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={8}
                          style={{
                            paddingRight: "40px",
                          }}
                        />
                        <i
                          className={`suffix-icon anticon ${
                            showPassword
                              ? "anticon-eye"
                              : "anticon-eye-invisible"
                          }`}
                          onClick={togglePasswordVisibility}
                          style={{
                            cursor: "pointer",
                            color: "#999",
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 3,
                          }}
                          title={
                            showPassword ? "Hide password" : "Show password"
                          }
                        ></i>
                      </div>
                      <div className="form-text">
                        Password must be at least 8 characters with uppercase,
                        lowercase, number, and special character.
                      </div>
                    </div>

                    <div className="form-group mb-4">
                      <label htmlFor="confirmPassword" className="form-label">
                        Confirm New Password
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          id="confirmPassword"
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          minLength={8}
                          style={{
                            paddingRight: "40px",
                          }}
                        />
                        <i
                          className={`suffix-icon anticon ${
                            showPassword
                              ? "anticon-eye"
                              : "anticon-eye-invisible"
                          }`}
                          onClick={togglePasswordVisibility}
                          style={{
                            cursor: "pointer",
                            color: "#999",
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 3,
                          }}
                          title={
                            showPassword ? "Hide password" : "Show password"
                          }
                        ></i>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary w-100 mb-3"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Resetting...
                        </>
                      ) : (
                        "Reset Password"
                      )}
                    </button>

                    <div className="text-center">
                      <a href="/login" className="text-decoration-none">
                        Back to Login
                      </a>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassForForgotPassword;
